import { sort, equal, not, and, readOnly, filterBy, filter, gte } from '@ember/object/computed';
import { computed } from '@ember/object';
import { isNone } from '@ember/utils';
import { attr, belongsTo } from '@ember-data/model';
import { fragment, fragmentArray } from 'ember-data-model-fragments/attributes';
import { inject as service } from '@ember/service';
import { clone } from '@square/dashboard-data/utils/ember-data-utils';
import Enum from '@square/dashboard-utilities/utils/enum';
import { isIn } from '@square/dashboard-utilities/macros';
import Money from '@square/dashboard-money/models/money';
import EmberDataBaseModel from '@square/dashboard-data/models/ember-data-base-model';
import { camelize } from '@square/dashboard-utilities/utils/keys';

// copied from dashboard/adapters/squareup/inventory/v3/purchase-order
const PURCHASE_ORDER_PATH = '/api/v2/inventory/purchase-orders';
export const PurchaseOrderState = new Enum(['PO_DO_NOT_USE', 'PO_DRAFT', 'PO_PENDING', 'PO_RECEIVING', 'PO_RECEIVED', 'PO_CANCELED', 'PO_AUTO']);
const ActionTypeToLineState = {
  ACTION_RECEIVE: 'PO_LINE_RECEIVED',
  ACTION_DAMAGE: 'PO_LINE_DAMAGED',
  ACTION_CANCEL: 'PO_LINE_CANCELED'
};
export const MAX_ROOT_LINE_ITEMS = 500;

// Top level (parent) lines are sorted in the order of their line number.
// Child lines should appear directly under the corresponding parent line, sorted by their line number
function LineItemSortFunction(left, right) {
  const leftNo = left.get('lineNumber');
  const rightNo = right.get('lineNumber');
  const leftParentNo = left.get('parentLineNumber');
  const rightParentNo = right.get('parentLineNumber');

  // Both lines are top-level non-child lines.
  if (!leftParentNo && !rightParentNo) {
    return leftNo - rightNo;
  }
  // Both lines are child lines.
  if (leftParentNo && rightParentNo) {
    return leftParentNo === rightParentNo ? rightNo - leftNo : leftParentNo - rightParentNo;
  }
  // Left is a child line, right is a non-child line.
  if (leftParentNo) {
    return leftParentNo - rightNo;
  }
  // Right is a child line, left is a non-child line.
  if (rightParentNo) {
    return leftNo - rightParentNo;
  }
  return 0;
}
const PurchaseOrder = EmberDataBaseModel.extend({
  store: service(),
  userInfo: service(),
  token: attr('string'),
  displayToken: attr('string'),
  lineItem: fragmentArray('squareup/inventory/v3/purchase-order-line'),
  charge: fragmentArray('squareup/inventory/v3/purchase-order-charge'),
  totalTaxAmount: attr('money-proto', {
    defaultValue: () => Money.zero()
  }),
  totalAmount: attr('money-proto', {
    defaultValue: () => Money.zero()
  }),
  state: attr('enum', {
    enumClass: PurchaseOrderState,
    defaultValue: PurchaseOrderState.PO_DRAFT
  }),
  stateChangedAt: attr('number'),
  stateChangedBy: belongsTo('employee', {
    async: true,
    inverse: null
  }),
  createdBy: belongsTo('employee', {
    async: true,
    inverse: null
  }),
  vendor: belongsTo('squareup/inventory/v3/vendor', {
    async: false
  }),
  draftVendor: attr('object'),
  destination: fragment('squareup/inventory/v3/purchase-order-destination'),
  tax: fragment('squareup/inventory/v3/catalog-object-fee', {
    defaultValue: null
  }),
  notes: attr('string'),
  expectedAt: attr('number'),
  schemaVersion: attr('number', {
    defaultValue: 0
  }),
  createdAt: attr('number'),
  updatedAt: attr('number'),
  chargeRemoved: false,
  // If a vendor save fails when creating a PO it's possible for a PO to have an embedded vendor with no token.
  // EmberData can't handle loading an embedded record without an id, so instead we move that data out of the payload
  // and add it as a new vendor here after the PO has been loaded.
  /* eslint-disable ember/no-side-effects */
  // eslint-disable-next-line ember/require-computed-property-dependencies
  loadVendor: computed('isLoaded', function () {
    if (this.draftVendor) {
      this.set('vendor', this.store.createRecord('squareup/inventory/v3/vendor', camelize(this.draftVendor)));
      this.set('draftVendor', null);
    }
    return true;
  }),
  /* eslint-enable ember/no-side-effects */

  isDirty: computed('chargeRemoved', 'hasDirtyAttributes', 'lineItem.hasDirtyAttributes', function () {
    if (this.chargeRemoved) {
      return true;
    }
    // Open issue with fragmentArray's hasDirtyAttributes not propagating correctly to the owning model
    // https://github.com/lytics/ember-data-model-fragments/issues/330
    if (this.lineItem.hasDirtyAttributes) {
      return true;
    }
    return this.hasDirtyAttributes;
  }),
  updated: computed('updatedAt', function () {
    if (!this.updatedAt) {
      return null;
    }
    return new Date(this.updatedAt);
  }),
  expected: computed('expectedAt', function () {
    if (!this.expectedAt) {
      return null;
    }
    return new Date(this.expectedAt);
  }),
  isPastDue: computed('expected', 'isActive', function () {
    if (this.isActive && this.expected) {
      const dateNow = new Date();
      // Reset to today's date at midnight.
      // PO is past due if the expected date is in the past (yesterday or older).
      dateNow.setHours(0, 0, 0, 0);
      return this.expected < dateNow;
    }
    return false;
  }),
  isAuto: equal('state', 'PO_AUTO'),
  isDraft: isIn('state', ['PO_DRAFT', 'PO_AUTO']),
  isNotDraft: not('isDraft'),
  isActive: isIn('state', ['PO_DRAFT', 'PO_AUTO', 'PO_PENDING', 'PO_RECEIVING']),
  isArchived: isIn('state', ['PO_RECEIVED', 'PO_CANCELED']),
  isReceivable: and('isActive', 'isNotDraft'),
  canEmail: and('isActive', 'isNotDraft'),
  currencyCode: readOnly('totalAmount.currencyCode'),
  // Total amount of the purchase order lines (without fee or tax).
  // Line items that have been damaged or that are canceled will not be included.
  subtotal: computed('lineItem.@each.{isAddingToTotal,amount}', 'currencyCode', function () {
    const linesAddingToTotal = (this.lineItem || []).filterBy('isAddingToTotal');
    return Money.sum(linesAddingToTotal.mapBy('amount').compact(), this.currencyCode);
  }),
  // Total amount of line items and fees without tax.
  subtotalWithFees: computed('subtotal', 'charge.@each.amount', 'currencyCode', function () {
    const chargeLines = this.charge || [];
    const feeTotal = Money.sum(chargeLines.mapBy('amount').compact(), this.currencyCode);
    return this.subtotal.add(feeTotal);
  }),
  // Total tax amount for the line items.
  taxTotal: computed('tax.{percentage,amount}', 'subtotal', 'currencyCode', function () {
    let taxTotal = Money.zero(this.currencyCode);
    if (this.tax?.percentage) {
      const subtotal = this.subtotal;
      const fraction = (this.tax?.percentage || 0) / 100;
      taxTotal = subtotal.multiply(fraction);
    } else if (this.tax?.amount) {
      taxTotal = this.tax?.amount;
    }
    return taxTotal;
  }),
  // Total tax amount for the fees.
  feeTaxTotal: computed('charge.@each.taxAmount', 'currencyCode', function () {
    const chargeLines = this.charge || [];
    return Money.sum(chargeLines.mapBy('taxAmount').compact(), this.currencyCode);
  }),
  pendingFees: filterBy('charge', 'isNew'),
  receivedLineItemQuantities: computed('receivedLineItem.@each.quantityFormatted', function () {
    return this.receivedLineItem.reduce((quantityMap, line) => {
      quantityMap[line.get('lineNumber')] = line.get('quantityFormatted');
      return quantityMap;
    }, {});
  }),
  feesWithoutReceivedQuantity: computed('charge.@each.associatedLineNumbers', 'receivedLineItemQuantities', function () {
    const lineItemQuantities = this.receivedLineItemQuantities;
    return this.charge.filter(charge => {
      const associatedLineNumbers = charge.get('associatedLineNumbers') || [];
      const associatedQuantity = associatedLineNumbers.reduce((runningQuantity, lineNumber) => {
        const lineQuantity = lineItemQuantities[lineNumber] || 0;
        return runningQuantity + lineQuantity;
      }, 0);
      return associatedQuantity === 0;
    });
  }),
  // Total due amount including fees and tax computed from the purchase order line items.
  totalDisplayAmount: computed('subtotalWithFees', 'taxTotal', 'feeTaxTotal', 'charge.@each.amount', function () {
    const subtotalWithFees = this.subtotalWithFees;
    const taxTotal = this.taxTotal;
    const feeTaxTotal = this.feeTaxTotal;
    return subtotalWithFees.add(taxTotal).add(feeTaxTotal);
  }),
  sortedLineItem: sort('lineItem', LineItemSortFunction),
  openLineItem: filterBy('lineItem', 'state', 'PO_LINE_OPEN'),
  sortedOpenLineItem: sort('openLineItem', LineItemSortFunction),
  receivedLineItem: filterBy('lineItem', 'state', 'PO_LINE_RECEIVED'),
  sortedReceivedLineItem: sort('receivedLineItem', LineItemSortFunction),
  newReceivedLineItem: filterBy('receivedLineItem', 'isNew'),
  canceledLineItem: filterBy('lineItem', 'state', 'PO_LINE_CANCELED'),
  newCanceledLineItem: filterBy('canceledLineItem', 'isNew'),
  damagedLineItem: filterBy('lineItem', 'state', 'PO_LINE_DAMAGED'),
  newDamagedLineItem: filterBy('damagedLineItem', 'isNew'),
  nonArchivedLineItem: filterBy('lineItem', 'isNotArchived'),
  rootLineItem: filter('lineItem', function (lineItem) {
    return isNone(lineItem.parentLineNumber);
  }),
  reachedMaxRootLineItems: gte('rootLineItem.length', MAX_ROOT_LINE_ITEMS),
  maxLineNumber: computed('lineItem.@each.lineNumber', function () {
    return Math.max(0, ...this.lineItem.mapBy('lineNumber'));
  }),
  nextLineNumber: computed('maxLineNumber', function () {
    return this.maxLineNumber + 1;
  }),
  pdfAddress: computed('token', function () {
    const poToken = this.token;
    return `${PURCHASE_ORDER_PATH}/${poToken}?pdf`;
  }),
  csvAddress: computed('token', function () {
    const poToken = this.token;
    return `${PURCHASE_ORDER_PATH}/${poToken}?csv`;
  }),
  performLineItemAction(item, actionType, quantity = 0) {
    if (item.get('isNew') || item.get('state') !== 'PO_LINE_OPEN') {
      return;
    }
    if (!quantity) {
      // If no quantity was specified, assume the action is performed on the whole quantity.
      quantity = item.get('quantity.count');
    }
    const parentItem = item;
    const initialQuantity = parentItem.get('quantity.count');
    parentItem.set('quantity.count', initialQuantity - quantity);
    parentItem.updateTotalCostAmount();
    if (parentItem.get('quantity.count') === 0) {
      parentItem.set('state', 'PO_LINE_ARCHIVED');
    }

    // Deep copy original line item.
    const childItem = clone(this.store, parentItem);
    childItem.set('quantity.count', quantity);
    childItem.set('state', ActionTypeToLineState[actionType]);
    childItem.set('parentLineNumber', parentItem.get('lineNumber'));
    childItem.set('lineNumber', this.nextLineNumber);
    childItem.updateTotalCostAmount();
    childItem.undoAction = function (purchaseOrder) {
      parentItem.set('quantity', parentItem.get('quantity').add(childItem.get('quantity')));
      parentItem.set('state', 'PO_LINE_OPEN');
      parentItem.updateTotalCostAmount();
      purchaseOrder.get('lineItem').removeObject(childItem);
      if (childItem.get('state') === 'PO_LINE_RECEIVED') {
        (purchaseOrder.get('charge') === undefined ? [] : purchaseOrder.get('charge')).forEach(fee => {
          fee.set('associatedLines', fee.get('associatedLines').rejectBy('lineNumber', childItem.get('lineNumber')));
        });
      }
    };
    this.lineItem.pushObject(childItem);
    if (childItem.get('state') === 'PO_LINE_RECEIVED') {
      (this.charge === undefined ? [] : this.charge).filterBy('isNew').forEach(fee => {
        fee.get('associatedLines').pushObject(this.store.createFragment('squareup/inventory/v3/purchase-order-charge-associated-line', {
          lineNumber: childItem.get('lineNumber')
        }));
      });
    }
  },
  cancel() {
    (this.lineItem || []).forEach(item => {
      this.performLineItemAction(item, 'ACTION_CANCEL');
    });
    this.set('state', 'PO_CANCELED');
    return this.save();
  },
  save() {
    if (!this.isDeleted) {
      this.set('totalAmount', this.totalDisplayAmount);
    }

    // if the destination of the PO is for a address-less mobile location, we default to the merchant's country code
    // this allows the save call to succeed without a full address
    if (!this.destination.address.countryCode) {
      this.destination.address.set('countryCode', this.userInfo.countryCode);
    }
    return this._super().then(purchaseOrder => {
      this.set('chargeRemoved', false);
      return purchaseOrder;
    });
  },
  rollbackAttributes() {
    this._super();
    this.set('chargeRemoved', false);
  }
});
PurchaseOrder.reopenClass({
  activeStates: ['PO_DRAFT', 'PO_AUTO', 'PO_PENDING', 'PO_RECEIVING'],
  archivedStates: ['PO_RECEIVED', 'PO_CANCELED'],
  _modelNameForMigration: 'squareup/inventory/v3/purchase-order'
});
export default PurchaseOrder;